/**
 * Toolbar dropdown controller — a thin wrapper over the shared openPopoverMenu
 * primitive (menu/modal unification, Phase 2). It replaces the bespoke
 * `.pb-open`-toggling controller the toolbar Save/Project/Export/Overflow menus
 * used to share.
 *
 * On open it relocates the pre-wired static menu markup into the shared
 * `.pb-popover-menu` shell (preserving every delegated action handler, the
 * auth-gated cloud group, and the inline rename input) and lets the shell own
 * positioning, focus-trap, aria-expanded, and outside-click / iframe-click /
 * Escape dismissal. On close the shell is removed and the menu node detaches —
 * its JS reference (and any cached child refs the caller holds) persist, so it
 * re-mounts unchanged on the next open.
 *
 * All toolbar menus share one popover owner, so opening any of them closes the
 * others (openPopoverMenu closes the same-owner menu on open).
 */

import { openPopoverMenu } from './popover-menu.js';

export const TOOLBAR_MENU_OWNER = 'toolbar-menu';

export class ToolbarMenu {
  /**
   * @param {HTMLElement} triggerEl — the button that toggles the menu (aria-expanded host).
   * @param {HTMLElement} menuEl — the pre-wired static menu node (relocated on open).
   * @param {{align?: 'left'|'right'|'center', width?: number,
   *          getAnchorRect?: () => DOMRect|Object|null, onOpen?: () => void}} [opts]
   */
  constructor(triggerEl, menuEl, opts = {}) {
    this._trigger = triggerEl;
    this._menu = menuEl;
    this._align = opts.align || 'right';
    this._width = opts.width;
    this._getAnchorRect = opts.getAnchorRect || null;
    this._onOpen = typeof opts.onOpen === 'function' ? opts.onOpen : null;
    this._handle = null;
    // When true, the next close skips restoring focus to the trigger.
    this._suppressRestore = false;

    this._trigger.addEventListener('click', (e) => {
      if (this._isDisabled(this._trigger)) return;
      e.stopPropagation();
      this.isOpen ? this.close() : this.open();
    });

    // Choosing an enabled item dismisses the menu. The action itself is wired by
    // separate delegated listeners on this same node; the rename <input> is not
    // a menuitem, so editing the project name never closes the menu.
    this._menu.addEventListener('click', (e) => {
      const item = e.target.closest('[role="menuitem"]');
      if (item && !this._isDisabled(item)) this.close();
    });

    // Tab dismisses the menu (returning focus to the trigger) rather than
    // cycling within it — preserves the prior toolbar-menu behavior over the
    // shared focus-trap's wrap-around.
    this._menu.addEventListener('keydown', (e) => {
      if (e.key === 'Tab') {
        e.preventDefault();
        e.stopPropagation();
        this.close();
      }
    });
  }

  get isOpen() {
    return this._handle != null;
  }

  open() {
    if (this.isOpen) return;
    this._onOpen?.();
    this._handle = openPopoverMenu(this._trigger, {
      owner: TOOLBAR_MENU_OWNER,
      align: this._align,
      width: this._width,
      role: 'menu',
      getAnchorRect: this._getAnchorRect || undefined,
      render: () => this._menu,
      restoreFocus: () => {
        if (this._suppressRestore) {
          this._suppressRestore = false;
          return;
        }
        if (typeof this._trigger?.focus === 'function' && document.body.contains(this._trigger)) {
          this._trigger.focus();
        }
      },
      onClose: () => {
        this._handle = null;
      }
    });
  }

  close() {
    // The openPopoverMenu handle IS the close function; no-op when not open.
    this._handle?.();
  }

  destroy() {
    this.close();
  }

  _isDisabled(el) {
    if (!el) return false;
    if (el.hasAttribute?.('disabled')) return true;
    return el.getAttribute('aria-disabled') === 'true';
  }
}
